<template>
    <div class="kakao-map" :style="{ height: mapHeight }">
        <div ref="mapElement" class="kakao-map__canvas"></div>
        <p v-if="showStatus && statusMessage" class="kakao-map__status">
            {{ statusMessage }}
        </p>
    </div>
</template>

<script>
import { loadKakaoMapSdk } from "@/utils/kakaoMapLoader";

export default {
    name: "KakaoMap",
    props: {
        appKey: {
            type: String,
            default: ""
        },
        center: {
            type: Object,
            default: () => ({
                lat: 37.566826,
                lng: 126.9786567
            })
        },
        level: {
            type: Number,
            default: 3
        },
        height: {
            type: [Number, String],
            default: 560
        },
        marker: {
            type: Boolean,
            default: true
        },
        addresses: {
            type: Array,
            default: () => []
        },
        fitBounds: {
            type: Boolean,
            default: true
        },
        markerTitle: {
            type: Boolean,
            default: true
        },
        libraries: {
            type: Array,
            default: () => []
        },
        options: {
            type: Object,
            default: () => ({})
        },
        showStatus: {
            type: Boolean,
            default: true
        }
    },
    emits: ["loaded", "error", "addresses-loaded"],
    data() {
        return {
            kakao: null,
            map: null,
            markerInstance: null,
            addressMarkerInstances: [],
            addressOverlayInstances: [],
            statusMessage: "지도를 불러오는 중입니다."
        };
    },
    computed: {
        mapHeight() {
            return typeof this.height === "number" ? `${this.height}px` : this.height;
        },
        sdkLibraries() {
            return Array.from(new Set([...this.libraries, "services"]));
        }
    },
    watch: {
        center: {
            deep: true,
            handler() {
                this.updateCenter();
            }
        },
        level() {
            if (this.map) {
                this.map.setLevel(this.level);
            }
        },
        marker() {
            this.updateCenterMarker();
        },
        addresses: {
            deep: true,
            handler() {
                this.updateAddressMarkers();
            }
        }
    },
    mounted() {
        this.initMap();
    },
    beforeUnmount() {
        this.clearAddressMarkers();
        if (this.markerInstance) {
            this.markerInstance.setMap(null);
        }
    },
    methods: {
        async initMap() {
            try {
                this.statusMessage = "지도를 불러오는 중입니다.";
                this.kakao = await loadKakaoMapSdk({
                    appKey: this.appKey,
                    libraries: this.sdkLibraries
                });

                const center = this.getLatLng(this.center);
                const mapOptions = {
                    center,
                    level: this.level,
                    ...this.options
                };

                this.map = new this.kakao.maps.Map(this.$refs.mapElement, mapOptions);
                this.updateCenterMarker();
                await this.updateAddressMarkers();
                this.statusMessage = "";
                this.$emit("loaded", {
                    kakao: this.kakao,
                    map: this.map,
                    marker: this.markerInstance,
                    addressMarkers: this.addressMarkerInstances
                });
            } catch (error) {
                this.statusMessage = error.message;
                this.$emit("error", error);
            }
        },
        getLatLng(point) {
            return new this.kakao.maps.LatLng(point.lat, point.lng);
        },
        normalizeAddress(addressItem) {
            if (typeof addressItem === "string") {
                return {
                    address: addressItem,
                    title: addressItem
                };
            }

            return {
                title: addressItem.title || addressItem.name || addressItem.address,
                ...addressItem
            };
        },
        searchAddress(geocoder, addressItem) {
            const item = this.normalizeAddress(addressItem);

            return new Promise((resolve) => {
                geocoder.addressSearch(item.address, (result, status) => {
                    if (status !== this.kakao.maps.services.Status.OK || !result.length) {
                        resolve({
                            ...item,
                            success: false,
                            status
                        });
                        return;
                    }

                    const firstResult = result[0];
                    const position = new this.kakao.maps.LatLng(firstResult.y, firstResult.x);

                    resolve({
                        ...item,
                        success: true,
                        lat: Number(firstResult.y),
                        lng: Number(firstResult.x),
                        position,
                        raw: firstResult
                    });
                });
            });
        },
        async updateAddressMarkers() {
            if (!this.map || !this.kakao) {
                return;
            }

            this.clearAddressMarkers();

            if (!this.addresses.length) {
                this.$emit("addresses-loaded", {
                    markers: [],
                    results: [],
                    failed: []
                });
                return;
            }

            if (!this.kakao.maps.services) {
                throw new Error("카카오 지도 services 라이브러리를 불러오지 못했습니다.");
            }

            this.statusMessage = "주소를 좌표로 변환하는 중입니다.";

            const geocoder = new this.kakao.maps.services.Geocoder();
            const results = await Promise.all(
                this.addresses.map((addressItem) => this.searchAddress(geocoder, addressItem))
            );
            const successResults = results.filter((item) => item.success);
            const failed = results.filter((item) => !item.success);
            const bounds = new this.kakao.maps.LatLngBounds();

            successResults.forEach((item) => {
                const marker = new this.kakao.maps.Marker({
                    position: item.position,
                    map: this.map,
                    title: item.title
                });

                this.addressMarkerInstances.push(marker);
                this.addAddressTitleOverlay(item);
                bounds.extend(item.position);
            });

            if (this.fitBounds && successResults.length) {
                this.map.setBounds(bounds);
            }

            this.statusMessage = "";
            this.$emit("addresses-loaded", {
                markers: this.addressMarkerInstances,
                results: successResults,
                failed
            });
        },
        clearAddressMarkers() {
            this.addressMarkerInstances.forEach((marker) => {
                marker.setMap(null);
            });
            this.addressOverlayInstances.forEach((overlay) => {
                overlay.setMap(null);
            });
            this.addressMarkerInstances = [];
            this.addressOverlayInstances = [];
        },
        addAddressTitleOverlay(item) {
            if (!this.markerTitle || !item.title) {
                return;
            }

            const titleElement = document.createElement("div");
            titleElement.className = "kakao-map__marker-title";
            titleElement.textContent = item.title;

            const overlay = new this.kakao.maps.CustomOverlay({
                content: titleElement,
                position: item.position,
                yAnchor: 2.55,
                zIndex: 3
            });

            overlay.setMap(this.map);
            this.addressOverlayInstances.push(overlay);
        },
        updateCenter() {
            if (!this.map || !this.kakao) {
                return;
            }

            const center = this.getLatLng(this.center);
            this.map.setCenter(center);
            this.updateCenterMarker();
        },
        updateCenterMarker() {
            if (!this.map || !this.kakao) {
                return;
            }

            if (!this.marker) {
                if (this.markerInstance) {
                    this.markerInstance.setMap(null);
                    this.markerInstance = null;
                }
                return;
            }

            const position = this.getLatLng(this.center);

            if (this.markerInstance) {
                this.markerInstance.setPosition(position);
                this.markerInstance.setMap(this.map);
                return;
            }

            this.markerInstance = new this.kakao.maps.Marker({
                position,
                map: this.map
            });
        },
        relayout() {
            if (this.map) {
                this.map.relayout();
                this.updateCenter();
            }
        }
    }
};
</script>

<style scoped>
.kakao-map {
    position: relative;
    overflow: hidden;
    width: 100%;
    min-height: 240px;
    border: 1px solid #d9e2ec;
    border-radius: 8px;
    background: #fff;
}

.kakao-map__canvas {
    width: 100%;
    height: 100%;
}

.kakao-map__status {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    padding: 24px;
    background: rgba(255, 255, 255, 0.86);
    color: #52606d;
    font-size: 15px;
    text-align: center;
}

:deep(.kakao-map__marker-title) {
    max-width: 180px;
    padding: 6px 10px;
    border: 1px solid #243b53;
    border-radius: 6px;
    background: #fff;
    color: #1f2933;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.35;
    text-align: center;
    white-space: nowrap;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.18);
}
</style>
